import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { get } from '../../helpers/httpClient';
import { toast } from 'react-toastify';
import General from './internal/General';
import Structure from './internal/Structure';
import Menu from './internal/Menu';
import Visual from './internal/Visual';
import Social from './internal/Social';
import GalleryLayouts from './internal/GalleryLayouts';

function WebsiteProfile() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [burgerOpen, setBurgerOpen] = useState(false);

  const tabFromQuery = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromQuery || 'structure');

  useEffect(() => {
    const nextTab = tabFromQuery || 'structure';
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromQuery]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError('');
    get(`websites/${id}`, (resp) => {
      if (!isMounted) return;
      // accept either resp.website or resp itself
      setWebsite(resp?.website || resp || null);
    })
      .catch(() => isMounted && toast.error('Failed to load website.'))
      .finally(() => isMounted && setLoading(false));
    return () => { isMounted = false; };
  }, [id, activeTab]);

  const handleSelect = (key) => {
    setBurgerOpen(false);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (key === 'structure') {
        p.delete('tab');
      } else {
        p.set('tab', key);
      }
      return p;
    });
  };

  const getModule = (tab) => {
    switch (tab) {
      case 'general':
        return General;
      case 'menu':
        return Menu;
      case 'visual':
        return Visual;
      case 'social':
        return Social;
      case 'gallery-layouts':
        return GalleryLayouts;
      case 'structure':
      default:
        return Structure;
    }
  };

  const CurrentModule = getModule(activeTab);

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h1>{website?.title || 'Website Profile'}</h1>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-4"><Spinner animation="border" /></div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : !website ? (
        <Alert variant="warning">Website not found.</Alert>
      ) : (
        <Row>
          <Col>
            <Card>
              <Card.Body>
                <div className="profile-tabs mb-3">
                  <div className="tabs-row" role="tablist" aria-label="Profile sections">
                    <button type="button" className={`login-button tab-btn ${activeTab === 'general' ? 'active' : ''}`} onClick={() => handleSelect('general')} role="tab" aria-selected={activeTab === 'general'}>General</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'structure' ? 'active' : ''}`} onClick={() => handleSelect('structure')} role="tab" aria-selected={activeTab === 'structure'}>Structure</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => handleSelect('menu')} role="tab" aria-selected={activeTab === 'menu'}>Menu</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'visual' ? 'active' : ''}`} onClick={() => handleSelect('visual')} role="tab" aria-selected={activeTab === 'visual'}>Visual</button>
                    <button type="button" className={`login-button tab-btn ${activeTab === 'social' ? 'active' : ''}`} onClick={() => handleSelect('social')} role="tab" aria-selected={activeTab === 'social'}>Social</button>
                  </div>

                  <div className={`tabs-burger ${burgerOpen ? 'open' : ''}`}>
                    <button type="button" className="login-button" aria-haspopup="listbox" aria-expanded={burgerOpen} id="tabs-burger-btn" onClick={() => setBurgerOpen(o => !o)}>
                      {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} ▾
                    </button>
                    <ul className="burger-menu" role="listbox" aria-labelledby="tabs-burger-btn">
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'general' ? 'active' : ''}`} onClick={() => handleSelect('general')}>General</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'structure' ? 'active' : ''}`} onClick={() => handleSelect('structure')}>Structure</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => handleSelect('menu')}>Menu</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'visual' ? 'active' : ''}`} onClick={() => handleSelect('visual')}>Visual</button></li>
                      <li><button type="button" className={`login-button tab-btn ${activeTab === 'social' ? 'active' : ''}`} onClick={() => handleSelect('social')}>Social</button></li>
                    </ul>
                  </div>
                </div>

                <CurrentModule website={website} onUpdated={(w) => setWebsite(w)} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default WebsiteProfile;
